import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { ToastrService } from 'ngx-toastr';
import { map, tap } from 'rxjs';
import { InfiniteResult, MitFieldInfo } from 'src/app/_models/body';
import { CustomerService } from 'src/app/_services/customer.service';
import { Global } from 'src/app/_services/global.service';
import { WhitelistService } from 'src/app/_services/whitelist.service';
import { InputFieldType } from 'src/app/common/constants';
import { cloneDeep } from 'lodash';
import { removeEmptyStringValues } from 'src/app/_helpers/utility';

@Component({
  selector: 'app-white-list-dialog',
  templateUrl: './white-list-dialog.component.html',
  styleUrls: ['./white-list-dialog.component.css']
})
export class WhiteListDialogComponent implements OnInit {
  readonly assetSourceArray: Array<any>;
  readonly rewardSource: Array<any>;
  readonly UploadModeOptions: Array<'Single' | 'Batch'> = ['Single', 'Batch'];
  formGroup: FormGroup
  dialogFields: Array<MitFieldInfo>
  whiteListModuleType: Array<string>;
  callback = () => this.onSubmit()
  currentOptions: Array<any>
  uploadMode: 'Single' | 'Batch' = 'Single'

  constructor(
    private customerService: CustomerService,
    private whitelistService: WhitelistService,
    private global: Global,
    private toastr: ToastrService,
    private nzModalRef: NzModalRef
  ) {

  }

  setDialogFields(moduleType: string, uploadMode: string): Array<MitFieldInfo> {
    const dialogFields: MitFieldInfo[] = [];

    // 处理上传模式相关的字段
    if (uploadMode === 'Batch') {
      dialogFields.push(this.getExcelUploadField());
    } else {
      dialogFields.push(...this.getManualInputFields());
    }

    // 添加通用字段
    dialogFields.push(this.getModuleTypeField());

    // 根据模块类型添加特定字段
    if (moduleType === 'Developer' || moduleType === 'Product') {
      dialogFields.push(this.getDeveloperProductIdField(moduleType));
    } else if (moduleType !== 'All' && moduleType !== 'AdminCreator') {
      dialogFields.push(this.getModuleDataIdField());
    }

    return dialogFields;
  }

  // 提取方法：Excel上传字段
  private getExcelUploadField(): MitFieldInfo {
    return {
      key: 'emailsExcel',
      label: 'Emails Excel (All emails in the first column of the first sheet, excluding the header)',
      type: InputFieldType.Uploader,
      size: 24,
      required: true,
      accept: '.xlsx, .xls, .csv'
    };
  }

  // 提取方法：手动输入字段
  private getManualInputFields(): MitFieldInfo[] {
    return [
      {
        key: 'userId',
        label: 'User ID (enter either userId or email)',
        type: InputFieldType.Selector,
        size: 12,
        selectorLabel: 'email',
        selectorSearchEvent: (ev) => this.getActiveCustomer(ev),
        options: this.currentOptions
      },
      {
        key: 'email',
        label: 'E-mail (enter either userId or email)',
        type: InputFieldType.Text,
        size: 12,
      }
    ];
  }

  // 提取方法：模块类型字段
  private getModuleTypeField(): MitFieldInfo {
    return {
      key: 'moduleType',
      label: 'Module Type',
      type: InputFieldType.Selector,
      size: 24,
      required: true,
      options: this.whiteListModuleType,
    };
  }

  // 提取方法：开发者ID字段
  private getDeveloperProductIdField(moduleType): MitFieldInfo {
    return {
      key: 'moduleDataId',
      label: `${moduleType} ID`,
      type: InputFieldType.Text,
      size: 24,
      required: true
    };
  }

  // 提取方法：模块数据ID字段
  private getModuleDataIdField(): MitFieldInfo {
    return {
      key: 'moduleDataId',
      label: 'Module Data ID',
      type: InputFieldType.Selector,
      size: 24,
      required: true,
      options: this.formGroup.value.moduleType === 'MmitaReward'
        ? this.rewardSource
        : this.assetSourceArray
    };
  }


  ngOnInit() {
    this.whiteListModuleType = this.global.section.WhiteListModuleTypeSelections
    this.setFormGroup()

  }


  setFormGroup() {
    if (this.uploadMode === 'Batch') {
      this.formGroup = new FormGroup({
        emailsExcel: new FormControl(null, Validators.required),
        moduleType: new FormControl('All', Validators.required),
        moduleDataId: new FormControl({ value: null, disabled: true }),
      });
    } else {
      this.formGroup = new FormGroup({
        userId: new FormControl(),
        email: new FormControl(),
        moduleType: new FormControl('All', Validators.required),
        moduleDataId: new FormControl({ value: null, disabled: true }),
      });
    }
    this.formGroup.get('moduleType').valueChanges.subscribe(moduleType => {
      this.formGroup.get('moduleDataId').setValidators(moduleType === 'All' || moduleType === 'AdminCreator' ? null : Validators.required)
      this.formGroup.get('moduleDataId').setValue(null)
      if (moduleType === 'All' || moduleType === 'AdminCreator') {
        this.formGroup.get('moduleDataId').disable()
      } else {
        this.formGroup.get('moduleDataId').enable()
      }
      this.dialogFields = this.setDialogFields(moduleType, this.uploadMode)
    })
    this.dialogFields = this.setDialogFields(this.formGroup.get('moduleType').value, this.uploadMode)
  }


  onSubmit() {
    if (this.uploadMode === 'Batch') {
      const _formGroup = cloneDeep(this.formGroup.value)
      _formGroup.emailsExcel = this.formGroup.value?.emailsExcel[0]?.file
      return this.whitelistService.addWhiteListByExcel(_formGroup).pipe(
        tap(data => {
          // 使用 tap 操作符处理副作用（如显示消息、关闭模态框）
          this.toastr.success(`Saved Successfully...!`, 'Success');
          this.nzModalRef.triggerOk();
        })
      );
    } else {
      if (!this.formGroup.value.userId && !this.formGroup.value.email) {
        this.toastr.error('You must enter either UserId or Email')
        return
      }
      const _formGroup = removeEmptyStringValues(cloneDeep(this.formGroup.value))
      return this.whitelistService.addWhiteList(_formGroup).pipe(
        tap(data => {
          // 使用 tap 操作符处理副作用（如显示消息、关闭模态框）
          this.toastr.success(`Saved Successfully...!`, 'Success');
          this.nzModalRef.triggerOk();
        })
      );
    }


  }


  getActiveCustomer(value?: string) {
    const prop = value ? { Email_Contains: value } : null;

    return this.customerService.getAll({ PageSize: 100, IsActive: true, ...prop }).pipe(
      // 使用 tap 执行副作用：将数据赋值给组件属性
      tap((res: InfiniteResult) => {
        this.currentOptions = res.data;
      }),
      // 使用 map 转换数据流：将 InfiniteResult 映射为其中的 data 数组
      map((res: InfiniteResult) => res.data)
    );
  }

  handleIndexChange(e: number): void {
    this.uploadMode = this.UploadModeOptions[e]
    this.setFormGroup()
    this.dialogFields = this.setDialogFields(this.formGroup.get('moduleType').value, this.uploadMode)

  }
}

